// Formatter helpers used across the app
// Exported as named functions so components can import only what's needed.

import i18n from '../i18n/config';
import { resolveLocale } from '../i18n/locales';

/**
 * Format bytes into a human readable string (KB, MB, GB, ...)
 * @param {number} bytes
 * @param {number} decimals
 * @returns {string}
 */
export function formatFileSize(bytes, decimals = 2) {
	if (bytes == null || !isFinite(bytes)) return '';
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
	const value = Math.abs(bytes) / Math.pow(k, i);
	const fixed = parseFloat(value.toFixed(decimals));
	return `${fixed} ${units[i]}`;
}

/**
 * Format an ISO date/time string into a readable locale string.
 * Uses Intl.DateTimeFormat where available for consistent formatting.
 * @param {string|Date} input - ISO string or Date object
 * @param {object} [opts] - optional Intl options or { locale, dateStyle, timeStyle }
 */
export function formatDate(input, opts = {}) {
	if (!input) return '';
	const date = input instanceof Date ? input : new Date(input);
	if (isNaN(date.getTime())) return '';

	const { locale = resolveLocale(i18n.resolvedLanguage || i18n.language), dateStyle = 'medium', timeStyle = 'short' } = opts;

	try {
		return new Intl.DateTimeFormat(locale, { dateStyle, timeStyle }).format(date);
	} catch (e) {
		// fallback
		return date.toLocaleString();
	}
}

/**
 * Small 'time ago' helper for recent timestamps.
 * Returns a compact string like "5s", "3m", "2h", "4d" or a formatted date for older entries.
 * @param {string|Date} input
 */
export function timeAgo(input) {
	if (!input) return '';
	const then = new Date(input).getTime();
	if (isNaN(then)) return '';
	const now = Date.now();
	const sec = Math.floor((now - then) / 1000);
	if (sec < 60) return `${sec}s`;
	const min = Math.floor(sec / 60);
	if (min < 60) return `${min}m`;
	const hr = Math.floor(min / 60);
	if (hr < 24) return `${hr}h`;
	const days = Math.floor(hr / 24);
	if (days < 7) return `${days}d`;

	// older than a week - show a human friendly date
	return formatDate(then, { dateStyle: 'medium', timeStyle: 'short' });
}

const formatters = {
	formatFileSize,
	formatDate,
	timeAgo
};

export default formatters;
